/**
 * 기술지원확약서 API 클라이언트 — backend /tech-support/* 와 1:1.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

export interface TechSupportInput {
  docNo: string;
  issueDate: string;
  recipient: string;
  businessName: string;
  supplier: string;
  ccRef: string;
  productName: string;
  /** 연동된 영업건 매출일련번호 (null=미연동). NAS 보관 경로를 만드는 데 쓴다. v2.94.0 */
  linkedSalesId?: string | null;
}

export interface TechSupportRow extends TechSupportInput {
  id: number;
  createdBy: string;
  createdAt: string;
  linkedSalesId: string | null;
  /** 연동된 영업건의 사업명. 번호는 있는데 이게 비어 있으면 그 영업건이 지워진 것이다. */
  linkedProjectName: string | null;
}

export interface TechSupportDetail extends TechSupportRow {
  updatedAt: string;
}

export interface TechSupportCreateResult {
  id: number;
  docNo: string;
}

async function unwrap<T>(r: Response): Promise<T> {
  const json = (await r.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function fetchTechSupportNextNo(date?: string): Promise<{ docNo: string; issueDate: string }> {
  const q = date ? `?date=${encodeURIComponent(date)}` : '';
  const r = await fetch(`${BASE}/tech-support/next-no${q}`, { credentials: 'include' });
  return unwrap<{ docNo: string; issueDate: string }>(r);
}

export async function listTechSupport(): Promise<TechSupportRow[]> {
  const r = await fetch(`${BASE}/tech-support`, { credentials: 'include' });
  const data = await unwrap<{ items: TechSupportRow[] }>(r);
  return data.items;
}

export async function getTechSupport(id: number): Promise<TechSupportDetail> {
  const r = await fetch(`${BASE}/tech-support/${id}`, { credentials: 'include' });
  return unwrap<TechSupportDetail>(r);
}

export async function createTechSupport(input: TechSupportInput): Promise<TechSupportCreateResult> {
  const r = await fetch(`${BASE}/tech-support`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return unwrap<TechSupportCreateResult>(r);
}

export async function updateTechSupport(id: number, input: Partial<TechSupportInput>): Promise<void> {
  const r = await fetch(`${BASE}/tech-support/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  await unwrap<{ id: number }>(r);
}

export async function deleteTechSupport(id: number): Promise<void> {
  const r = await fetch(`${BASE}/tech-support/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await unwrap<{ id: number }>(r);
}

/**
 * 영업건 연동 걸기·풀기 (v2.94.0).
 *
 * ⚠ updateTechSupport(PATCH /:id) 로는 **연동을 풀 수 없다** — 서버가 COALESCE 로 처리해
 *   빈 값을 「안 바꿈」으로 읽는다. 걸기와 풀기가 대칭이어야 하므로 전용 통로를 쓴다.
 */
export async function linkTechSupportToSales(
  id: number,
  salesId: string | null,
): Promise<{ linkedSalesId: string | null; linkedProjectName: string | null }> {
  const r = await fetch(`${BASE}/tech-support/${id}/link-sales`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ salesId }),
  });
  return unwrap<{ id: number; linkedSalesId: string | null; linkedProjectName: string | null }>(r);
}

export function techSupportPdfUrl(id: number): string {
  return `${BASE}/tech-support/${id}/download.pdf`;
}

/** 채워진 워드 양식(로고·인감 포함) 다운로드 URL */
export function techSupportDocxUrl(id: number): string {
  return `${BASE}/tech-support/${id}/download.docx`;
}
